/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * NotificationControlsPage
 * -------------------------
 * Admin page for managing notification channels and templates for an application.
 * Two-column layout (channels left, templates right).
 *
 * Key UX decisions (Steve Krug — "Don't Make Me Think"):
 * - Already-configured channels are greyed out in the dropdown with "Already exists" label.
 * - Channel config uses dynamic, human-friendly form fields instead of raw JSON.
 * - Template form shows inline validation errors, pre-populates subject/body on edit.
 * - Template variable syntax ({{variable}}) is explained with contextual help text.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Select from 'react-select';
import { useTheme } from '../theme/ThemeContext';
import APIHelper from '../api/APIHelper';
import envConfig from '../config/envConfig';
import { useAlertDialog } from '../components/common/AlertDialogContext';
import ProgressBarDialog from '../components/common/ProgressBar';
import Paginator from '../components/common/Paginator';

const apiHelper = new APIHelper({
    apiUrl: envConfig.backendApiUrl,
    tokenUrl: envConfig.backendTokenUrl,
    clientId: envConfig.clientKey,
    clientSecret: envConfig.clientSecret
});

const CHANNEL_TYPES = [
    { value: 'in_app',    label: 'In-App',   icon: 'bi-app-indicator', hasConfig: false },
    { value: 'email',     label: 'Email',    icon: 'bi-envelope',      hasConfig: true  },
    { value: 'websocket', label: 'WebSocket',icon: 'bi-broadcast',     hasConfig: false },
    { value: 'sms',       label: 'SMS',      icon: 'bi-chat-dots',     hasConfig: true, frozen: true, frozenLabel: 'Coming Soon' },
];

// Channels shown in the notification template channel selector
// (webhook excluded — handled via Event Trigger call_webhook action)
// (sms included but frozen/disabled — coming soon)
const TEMPLATE_CHANNEL_TYPES = CHANNEL_TYPES;

const CHANNEL_ICONS = Object.fromEntries(CHANNEL_TYPES.map(c => [c.value, c.icon]));

const EMAIL_PROVIDERS = [
    { value: 'smtp', label: 'SMTP' },
    { value: 'aws_ses', label: 'AWS SES' },
    { value: 'sendgrid', label: 'SendGrid' },
    { value: 'mailgun', label: 'Mailgun' }
];

function getConfigFields(channelType, provider) {
    if (channelType === 'email') {
        const common = [{ key: 'from_email', label: 'From Email', type: 'email', required: true, placeholder: 'noreply@example.com' }];
        if (provider === 'smtp') return [
            { key: 'provider', type: 'hidden', value: 'smtp' },
            { key: 'host', label: 'SMTP Host', type: 'text', placeholder: 'leave blank to use the platform mailbox' },
            { key: 'port', label: 'Port', type: 'number', placeholder: '587' },
            { key: 'username', label: 'Username', type: 'text', placeholder: 'leave blank to use the platform mailbox' },
            { key: 'password', label: 'Password', type: 'password', placeholder: 'leave blank to use the platform mailbox' },
            { key: 'use_tls', label: 'Use TLS', type: 'checkbox', default: true },
            { key: 'from_email', label: 'From Email', type: 'email', placeholder: 'leave blank to use the platform mailbox' },
        ];
        if (provider === 'aws_ses') return [
            { key: 'provider', type: 'hidden', value: 'aws_ses' },
            { key: 'region', label: 'AWS Region', type: 'text', required: true, placeholder: 'us-east-1' },
            { key: 'access_key', label: 'Access Key ID', type: 'text', required: true, placeholder: 'AKIA...' },
            { key: 'secret_key', label: 'Secret Access Key', type: 'password', required: true, placeholder: '••••••' },
            ...common
        ];
        if (provider === 'sendgrid') return [
            { key: 'provider', type: 'hidden', value: 'sendgrid' },
            { key: 'api_key', label: 'API Key', type: 'password', required: true, placeholder: 'SG.xxx...' },
            ...common
        ];
        if (provider === 'mailgun') return [
            { key: 'provider', type: 'hidden', value: 'mailgun' },
            { key: 'api_key', label: 'API Key', type: 'password', required: true, placeholder: 'key-xxx...' },
            { key: 'domain', label: 'Domain', type: 'text', required: true, placeholder: 'mg.example.com' },
            ...common
        ];
        return common;
    }
    if (channelType === 'sms') return [
        { key: 'provider', type: 'hidden', value: 'twilio' },
        { key: 'account_sid', label: 'Account SID', type: 'text', required: true, placeholder: 'AC...' },
        { key: 'auth_token', label: 'Auth Token', type: 'password', required: true, placeholder: '••••••' },
        { key: 'from_number', label: 'From Number', type: 'text', required: true, placeholder: '+1234567890' }
    ];
    if (channelType === 'webhook') return [
        { key: 'url', label: 'Webhook URL', type: 'url', required: true, placeholder: 'https://example.com/webhook' },
        { key: 'timeout', label: 'Timeout (seconds)', type: 'number', required: false, placeholder: '10' },
        { key: 'secret_header', label: 'Secret Header Name', type: 'text', required: false, placeholder: 'X-Secret (optional)' },
        { key: 'secret_value', label: 'Secret Header Value', type: 'password', required: false, placeholder: '••••••' }
    ];
    return [];
}

function configToFieldValues(config, channelType) {
    if (!config || typeof config !== 'object') return {};
    const values = { ...config };
    if (channelType === 'webhook' && config.headers) {
        const headerEntries = Object.entries(config.headers);
        if (headerEntries.length > 0) {
            values.secret_header = headerEntries[0][0];
            values.secret_value = headerEntries[0][1];
        }
    }
    return values;
}

function fieldValuesToConfig(values, channelType, provider) {
    const fields = getConfigFields(channelType, provider);
    const config = {};
    for (const field of fields) {
        if (field.type === 'hidden') {
            config[field.key] = field.value;
        } else if (field.key === 'secret_header' || field.key === 'secret_value') {
            continue;
        } else if (field.type === 'number') {
            config[field.key] = values[field.key] ? parseInt(values[field.key], 10) : undefined;
        } else if (field.type === 'checkbox') {
            config[field.key] = values[field.key] ?? field.default ?? false;
        } else {
            if (values[field.key]) config[field.key] = values[field.key];
        }
    }
    if (channelType === 'webhook' && values.secret_header && values.secret_value) {
        config.headers = { [values.secret_header]: values.secret_value };
    }
    return config;
}

export default function NotificationControlsPage() {
    const { theme } = useTheme();
    const { id: applicationId } = useParams();
    const navigate = useNavigate();
    const { showAlertDialog } = useAlertDialog();

    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('templates');
    const [currentPage, setCurrentPage] = useState(1);
    const [channels, setChannels] = useState([]);
    const [events, setEvents] = useState([]);
    const [triggers, setTriggers] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [contextKeys, setContextKeys] = useState(null);
    const [refreshKey, setRefreshKey] = useState(0);

    // Channel form state
    const [showChannelForm, setShowChannelForm] = useState(false);
    const [editingChannel, setEditingChannel] = useState(null);
    const [channelType, setChannelType] = useState('');
    const [channelProvider, setChannelProvider] = useState('');
    const [channelConfigValues, setChannelConfigValues] = useState({});
    const [channelErrors, setChannelErrors] = useState({});

    // Template Modal state
    const [showTemplateModal, setShowTemplateModal] = useState(false);
    const [editingTemplate, setEditingTemplate] = useState(null);
    const [templateForm, setTemplateForm] = useState({
        name: '', trigger: '', subject_template: '', body_template: '',
        channels: [], is_active: true
    });
    const [templateErrors, setTemplateErrors] = useState({});

    const [totalRecords, setTotalRecords] = useState(0);
    const itemsPerPage = 5;

    const fetchStaticData = useCallback(async () => {
        setLoading(true);
        try {
            const [chResp, evResp, trResp, ckResp] = await Promise.all([
                apiHelper.apiCall('/arcee/notification-channels/', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ application_id: parseInt(applicationId, 10) })
                }),
                apiHelper.apiCall('/arcee/events/', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ application_id: parseInt(applicationId, 10) })
                }),
                apiHelper.apiCall('/arcee/event-triggers/?no_page=true', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ application_id: parseInt(applicationId, 10) })
                }),
                apiHelper.apiCall('/arcee/events/context-keys/', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ application_id: parseInt(applicationId, 10) })
                })
            ]);

            if (chResp.ok) {
                const data = await chResp.json();
                setChannels(Array.isArray(data) ? data : (data.results || []));
            }
            if (evResp.ok) {
                const data = await evResp.json();
                setEvents(Array.isArray(data) ? data : (data.results || []));
            }
            if (trResp && trResp.ok) {
                const data = await trResp.json();
                setTriggers(Array.isArray(data) ? data : (data.results || []));
            }
            if (ckResp && ckResp.ok) {
                const data = await ckResp.json();
                setContextKeys(data);
            }
        } catch (error) {
            showAlertDialog({ title: 'Error', message: error.message, type: 'error' });
        } finally {
            setLoading(false);
        }
    }, [applicationId, showAlertDialog]);

    const fetchTemplates = useCallback(async () => {
        try {
            const tmResp = await apiHelper.apiCall(`/arcee/notification-templates/?page=${currentPage}&page_size=${itemsPerPage}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ application_id: parseInt(applicationId, 10) })
            });

            if (tmResp.ok) {
                const data = await tmResp.json();
                setTemplates(data.results || []);
                setTotalRecords(data.count || 0);
            }
        } catch (error) {
            console.error("Failed to fetch templates", error);
        }
    }, [applicationId, currentPage, itemsPerPage]);

    useEffect(() => { fetchStaticData(); }, [fetchStaticData, refreshKey]);
    useEffect(() => { fetchTemplates(); }, [fetchTemplates, refreshKey]);

    const configuredTypes = channels.map(ch => ch.channel_type);
    const availableTypes = CHANNEL_TYPES.filter(ct => !configuredTypes.includes(ct.value));

    // ── Channel CRUD ──────────────────────────────
    const resetChannelForm = () => {
        setChannelType('');
        setChannelProvider('');
        setChannelConfigValues({});
        setChannelErrors({});
        setEditingChannel(null);
        setShowChannelForm(false);
    };

    const openEditChannel = (ch) => {
        setChannelType(ch.channel_type);
        const provider = ch.config?.provider || '';
        setChannelProvider(provider);
        setChannelConfigValues(configToFieldValues(ch.config || {}, ch.channel_type));
        setChannelErrors({});
        setEditingChannel(ch);
        setShowChannelForm(true);
    };

    const validateChannelForm = () => {
        const errs = {};
        if (!channelType) errs.channel_type = 'Please select a channel type.';
        const ct = CHANNEL_TYPES.find(c => c.value === channelType);
        if (ct?.hasConfig) {
            const provider = channelType === 'email' ? channelProvider : undefined;
            if (channelType === 'email' && !channelProvider) {
                errs.provider = 'Please select an email provider.';
            }
            const fields = getConfigFields(channelType, provider);
            for (const f of fields) {
                if (f.required && f.type !== 'hidden' && !channelConfigValues[f.key]) {
                    errs[f.key] = `${f.label} is required.`;
                }
            }
        }
        setChannelErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const doSaveChannel = async () => {
        const ct = CHANNEL_TYPES.find(c => c.value === channelType);
        let config = {};
        if (ct?.hasConfig) {
            const provider = channelType === 'email' ? channelProvider : undefined;
            config = fieldValuesToConfig(channelConfigValues, channelType, provider);
        }

        const payload = {
            application: parseInt(applicationId, 10),
            channel_type: channelType,
            enabled: editingChannel ? editingChannel.enabled : true,
            config
        };

        try {
            let resp;
            if (editingChannel) {
                resp = await apiHelper.apiCall(`/arcee/notification-channels/${editingChannel.id}/`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload)
                });
            } else {
                resp = await apiHelper.apiCall('/arcee/notification-channels/create/', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload)
                });
            }
            if (!resp.ok) {
                const err = await resp.json().catch(() => ({}));
                throw new Error(err.error || JSON.stringify(err) || 'Failed to save channel');
            }
            resetChannelForm();
            setRefreshKey(k => k + 1);
        } catch (error) {
            showAlertDialog({ title: 'Error', message: error.message, type: 'error' });
        }
    };

    const handleSaveChannel = () => {
        if (!validateChannelForm()) return;
        doSaveChannel();
    };

    const handleDeleteChannel = (ch) => {
        showAlertDialog({
            title: 'Delete Channel',
            message: `Are you sure you want to delete the "${(ch.channel_type || '').replace(/_/g, ' ')}" channel? This cannot be undone.`,
            type: 'warning',
            showCancel: true,
            onOk: async () => {
                try {
                    const resp = await apiHelper.apiCall(`/arcee/notification-channels/${ch.id}/`, { method: 'DELETE' });
                    if (!resp.ok) throw new Error('Failed to delete');
                    setRefreshKey(k => k + 1);
                } catch (error) {
                    showAlertDialog({ title: 'Error', message: error.message, type: 'error' });
                }
            }
        });
    };

    const handleToggleChannel = async (ch) => {
        try {
            const resp = await apiHelper.apiCall(`/arcee/notification-channels/${ch.id}/`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ enabled: !ch.enabled })
            });
            if (!resp.ok) throw new Error('Failed to toggle');
            setRefreshKey(k => k + 1);
        } catch (error) {
            showAlertDialog({ title: 'Error', message: error.message, type: 'error' });
        }
    };

    const FieldError = ({ error }) => error ? (
        <div className="text-danger mt-1" style={{ fontSize: '12px' }}>
            <i className="bi bi-exclamation-circle me-1"></i>{error}
        </div>
    ) : null;

    const renderConfigFields = () => {
        const ct = CHANNEL_TYPES.find(c => c.value === channelType);
        if (!ct || !ct.hasConfig) return null;

        if (channelType === 'email' && !channelProvider) {
            return (
                <div className="mb-3">
                    <label className="small fw-bold text-muted mb-1">Email Provider</label>
                    <select className="form-select form-select-sm"
                        value={channelProvider}
                        onChange={e => { setChannelProvider(e.target.value); setChannelConfigValues(v => ({ ...v, provider: e.target.value })); }}
                        style={{ borderRadius: theme.inputRadius || '6px' }}>
                        <option value="">Select provider...</option>
                        {EMAIL_PROVIDERS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                    </select>
                    <FieldError error={channelErrors.provider} />
                </div>
            );
        }

        const provider = channelType === 'email' ? channelProvider : undefined;
        const fields = getConfigFields(channelType, provider);

        return (
            <>
                {channelType === 'email' && (
                    <div className="mb-3">
                        <label className="small fw-bold text-muted mb-1">Email Provider</label>
                        <select className="form-select form-select-sm"
                            value={channelProvider}
                            onChange={e => { setChannelProvider(e.target.value); setChannelConfigValues({ provider: e.target.value }); }}
                            disabled={!!editingChannel}
                            style={{ borderRadius: theme.inputRadius || '6px' }}>
                            <option value="">Select provider...</option>
                            {EMAIL_PROVIDERS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                        </select>
                        <FieldError error={channelErrors.provider} />
                    </div>
                )}
                {fields.filter(f => f.type !== 'hidden').map(field => (
                    <div className="mb-3" key={field.key}>
                        {field.type === 'checkbox' ? (
                            <div className="form-check">
                                <input type="checkbox" className="form-check-input"
                                    checked={channelConfigValues[field.key] ?? field.default ?? false}
                                    onChange={e => setChannelConfigValues(v => ({ ...v, [field.key]: e.target.checked }))}
                                    id={`ch-${field.key}`} />
                                <label className="form-check-label small" htmlFor={`ch-${field.key}`}>{field.label}</label>
                            </div>
                        ) : (
                            <>
                                <label className="small fw-bold text-muted mb-1">{field.label}{field.required && <span className="text-danger ms-1">*</span>}</label>
                                <input
                                    type={field.type}
                                    className={`form-control form-control-sm${channelErrors[field.key] ? ' is-invalid' : ''}`}
                                    value={channelConfigValues[field.key] || ''}
                                    onChange={e => setChannelConfigValues(v => ({ ...v, [field.key]: e.target.value }))}
                                    placeholder={field.placeholder}
                                    style={{ borderRadius: theme.inputRadius || '6px' }}
                                />
                                <FieldError error={channelErrors[field.key]} />
                            </>
                        )}
                    </div>
                ))}
            </>
        );
    };

    // ── Template CRUD ──────────────────────────────
    const resetTemplateForm = () => {
        setTemplateForm({ name: '', trigger: '', subject_template: '', body_template: '', channels: [], is_active: true });
        setTemplateErrors({});
        setEditingTemplate(null);
        setShowTemplateModal(false);
    };

    const openEditTemplate = (t) => {
        setTemplateForm({
            name: t.name || '',
            trigger: t.trigger || '',
            subject_template: t.subject_template || '',
            body_template: t.body_template || '',
            channels: t.channels || [],
            is_active: t.is_active !== false
        });
        setTemplateErrors({});
        setEditingTemplate(t);
        setShowTemplateModal(true);
    };

    const handleTemplateChannelToggle = (chType) => {
        setTemplateForm(prev => ({
            ...prev,
            channels: prev.channels.includes(chType)
                ? prev.channels.filter(c => c !== chType)
                : [...prev.channels, chType]
        }));
    };

    const validateTemplateForm = () => {
        const errs = {};
        if (!templateForm.name.trim()) errs.name = 'Template name is required.';
        if (!templateForm.trigger) errs.trigger = 'Trigger is required.';
        if (!templateForm.subject_template.trim()) errs.subject_template = 'Subject template is required.';
        if (!templateForm.body_template.trim()) errs.body_template = 'Body template is required.';
        if (templateForm.channels.length === 0) errs.channels = 'Select at least one channel.';
        setTemplateErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleSaveTemplate = async () => {
        if (!validateTemplateForm()) return;
        const payload = {
            application: parseInt(applicationId, 10),
            name: templateForm.name,
            trigger: parseInt(templateForm.trigger, 10),
            subject_template: templateForm.subject_template,
            body_template: templateForm.body_template,
            channels: templateForm.channels,
            is_active: templateForm.is_active
        };
        try {
            let resp;
            if (editingTemplate) {
                resp = await apiHelper.apiCall(`/arcee/notification-templates/${editingTemplate.id}/`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload)
                });
            } else {
                resp = await apiHelper.apiCall('/arcee/notification-templates/create/', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload)
                });
            }
            if (!resp.ok) {
                const err = await resp.json().catch(() => ({}));
                if (err && typeof err === 'object' && !err.error) {
                    const backendErrs = {};
                    for (const [key, val] of Object.entries(err)) {
                        backendErrs[key] = Array.isArray(val) ? val.join(', ') : String(val);
                    }
                    if (Object.keys(backendErrs).length > 0) {
                        setTemplateErrors(prev => ({ ...prev, ...backendErrs }));
                        return;
                    }
                }
                throw new Error(err.error || 'Failed to save template. Please try again.');
            }
            resetTemplateForm();
            fetchTemplates();
        } catch (error) {
            showAlertDialog({ title: 'Error', message: error.message, type: 'error' });
        }
    };

    const handleDeleteTemplate = (t) => {
        showAlertDialog({
            title: 'Delete Template',
            message: `Are you sure you want to delete template "${t.name}"? This cannot be undone.`,
            type: 'warning',
            showCancel: true,
            onOk: async () => {
                try {
                    const resp = await apiHelper.apiCall(`/arcee/notification-templates/${t.id}/`, { method: 'DELETE' });
                    if (!resp.ok) throw new Error('Failed to delete');
                    setRefreshKey(k => k + 1);
                } catch (error) {
                    showAlertDialog({ title: 'Error', message: error.message, type: 'error' });
                }
            }
        });
    };

    const handleToggleTemplate = async (t) => {
        try {
            const resp = await apiHelper.apiCall(`/arcee/notification-templates/${t.id}/`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ is_active: !t.is_active })
            });
            if (!resp.ok) throw new Error('Failed to toggle');
            setRefreshKey(k => k + 1);
        } catch (error) {
            showAlertDialog({ title: 'Error', message: error.message, type: 'error' });
        }
    };

    return (
        <div className="d-flex flex-column w-100" style={{ background: '#f8fafc', minHeight: '100vh', overflowX: 'hidden' }}>
            {/* Header */}
            <div className="w-100 px-3 py-3 px-md-4 py-md-4 bg-white border-bottom d-flex flex-wrap align-items-center justify-content-between gap-3 shadow-sm" style={{ zIndex: 10 }}>
                <div className="d-flex align-items-center gap-3">
                    <button
                        className="btn btn-light rounded-circle shadow-sm"
                        onClick={() => navigate(`/dashboard/applications/${applicationId}`)}
                        style={{ width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    >
                        <i className="bi bi-arrow-left"></i>
                    </button>
                    <div>
                        <h4 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2">
                            <i className="bi bi-bell"></i>
                            Notification Controls
                        </h4>
                        <small className="text-muted d-none d-md-block">
                            Configure how and when notifications are delivered to users.
                        </small>
                    </div>
                </div>

                <div className="d-flex align-items-center gap-2">
                    <button
                        className="btn d-flex align-items-center gap-2"
                        onClick={() => {
                            const isNested = window.location.pathname.includes('/notification-analytics/');
                            if (isNested) {
                                navigate(`/dashboard/applications/${applicationId}/notification-analytics`);
                            } else {
                                navigate(`/dashboard/applications/${applicationId}/notification-controls/notification-analytics`);
                            }
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = theme.primary;
                            e.currentTarget.style.color = '#fff';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.color = theme.primary;
                        }}
                        style={{
                            borderRadius: theme.buttonRadius || '8px',
                            border: `1px solid ${theme.primary}`,
                            color: theme.primary,
                            background: 'transparent',
                            transition: 'all 0.2s ease-in-out'
                        }}
                    >
                        <i className="bi bi-graph-up"></i>
                        Analytics
                    </button>
                </div>
            </div>

            <div className="container-fluid p-3 p-md-4" style={{ maxWidth: 1400 }}>
                {/* Tabs Navigation */}
                <div className="nav nav-pills mb-4 bg-white p-2 rounded shadow-sm border d-inline-flex" role="tablist">
                    <button
                        className={`nav-link fw-semibold px-4 py-2 ${activeTab === 'templates' ? 'active shadow-sm' : 'text-muted'}`}
                        onClick={() => setActiveTab('templates')}
                        style={{
                            borderRadius: theme.buttonRadius || '6px',
                            backgroundColor: activeTab === 'templates' ? theme.primary : 'transparent',
                            color: activeTab === 'templates' ? '#fff' : 'inherit',
                            transition: 'all 0.2s ease'
                        }}
                    >
                        <i className="bi bi-file-earmark-text me-2"></i> Templates
                    </button>
                    <button
                        className={`nav-link fw-semibold px-4 py-2 ms-2 ${activeTab === 'channels' ? 'active shadow-sm' : 'text-muted'}`}
                        onClick={() => setActiveTab('channels')}
                        style={{
                            borderRadius: theme.buttonRadius || '6px',
                            backgroundColor: activeTab === 'channels' ? theme.primary : 'transparent',
                            color: activeTab === 'channels' ? '#fff' : 'inherit',
                            transition: 'all 0.2s ease'
                        }}
                    >
                        <i className="bi bi-broadcast-pin me-2"></i> Channels
                    </button>
                </div>

                {loading ? <ProgressBarDialog open={true} text_message="Loading..." /> : (
                    <>
                        {/* ── Templates Tab ───────────────────────── */}
                        {activeTab === 'templates' && (() => {
                            const pageCount = Math.max(1, Math.ceil(totalRecords / itemsPerPage));
                            const handlePageChange = (newPage) => {
                                if (newPage >= 1 && newPage <= pageCount) setCurrentPage(newPage);
                            };
                            const handlePrevious = () => setCurrentPage(p => Math.max(p - 1, 1));
                            const handleNext = () => setCurrentPage(p => Math.min(p + 1, pageCount));

                            return (
                                <div>
                                    <div className="d-flex align-items-center justify-content-between mb-4 mt-2">
                                        <div>
                                            <h5 className="fw-bold mb-1">Notification Templates</h5>
                                            <p className="text-muted small mb-0">Manage notification content mapped to specific triggers.</p>
                                        </div>
                                        <button
                                            className="btn btn-primary d-flex align-items-center gap-2 shadow-sm"
                                            onClick={() => { resetTemplateForm(); setShowTemplateModal(true); }}
                                            style={{ background: theme.primary, border: 'none', borderRadius: theme.buttonRadius || '6px' }}
                                        >
                                            <i className="bi bi-plus-lg"></i>
                                            Add Template
                                        </button>
                                    </div>
                                    <div className="card border-0 shadow-sm" style={{ borderRadius: theme.cardRadius }}>
                                        <div className="table-responsive">
                                            <table className="table table-hover align-middle mb-0">
                                                <thead className="bg-light">
                                                    <tr>
                                                        <th className="px-4 py-3 text-muted text-uppercase small fw-bold border-0">Template Name</th>
                                                        <th className="px-4 py-3 text-muted text-uppercase small fw-bold border-0">Trigger Binding</th>
                                                        <th className="px-4 py-3 text-muted text-uppercase small fw-bold border-0">Channels</th>
                                                        <th className="px-4 py-3 text-muted text-uppercase small fw-bold border-0 text-center">Status</th>
                                                        <th className="px-4 py-3 text-muted text-uppercase small fw-bold border-0 text-end">Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="border-top-0">
                                                    {templates.length === 0 ? (
                                                        <tr>
                                                            <td colSpan="5" className="text-center py-5 text-muted">
                                                                <i className="bi bi-inbox fs-2 mb-2 d-block opacity-50"></i>
                                                                No templates configured yet.
                                                            </td>
                                                        </tr>
                                                    ) : (
                                                        templates.map(t => (
                                                            <tr key={t.id}>
                                                                <td className="px-4 py-3">
                                                                    <div className="fw-bold text-dark text-truncate" style={{ maxWidth: 220 }} title={t.name}>
                                                                        {t.name}
                                                                    </div>
                                                                    <div className="text-muted small text-truncate" style={{ maxWidth: 220 }} title={t.subject_template}>
                                                                        {t.subject_template}
                                                                    </div>
                                                                </td>
                                                                <td className="px-4 py-3">
                                                                    <code className="text-primary bg-light px-2 py-1 rounded">
                                                                        {(() => {
                                                                            const tObj = triggers.find(tr => tr.id === t.trigger) || (t.trigger && typeof t.trigger === 'object' ? t.trigger : null);

                                                                            return tObj ? tObj.name : `Trigger ID: ${t.trigger}`;
                                                                        })()}
                                                                    </code>
                                                                </td>
                                                                <td className="px-4 py-3">
                                                                    <div className="d-flex gap-1 flex-wrap">
                                                                        {(t.channels || []).map(ch => (
                                                                            <span key={ch} className="badge bg-light text-dark border px-2 py-1 text-capitalize fw-normal" style={{ fontSize: '11px' }}>
                                                                                <i className={`bi ${CHANNEL_ICONS[ch] || 'bi-bell'} me-1`}></i>
                                                                                {ch.replace(/_/g, ' ')}
                                                                            </span>
                                                                        ))}
                                                                    </div>
                                                                </td>
                                                                <td className="px-4 py-3 text-center">
                                                                    <span className="badge rounded-pill" style={{
                                                                        background: t.is_active !== false ? '#dcfce7' : '#fee2e2',
                                                                        color: t.is_active !== false ? '#166534' : '#991b1b',
                                                                        fontWeight: '500'
                                                                    }}>
                                                                        {t.is_active !== false ? 'Active' : 'Inactive'}
                                                                    </span>
                                                                </td>
                                                                <td className="px-4 py-3 text-end">
                                                                    <button className="btn btn-sm btn-light me-2" onClick={() => handleToggleTemplate(t)} title={t.is_active !== false ? 'Deactivate' : 'Activate'}>
                                                                        <i className={`bi ${t.is_active !== false ? 'bi-toggle-on text-success' : 'bi-toggle-off text-muted'}`}></i>
                                                                    </button>
                                                                    <button className="btn btn-sm btn-light text-primary me-2" onClick={() => openEditTemplate(t)}>
                                                                        <i className="bi bi-pencil"></i>
                                                                    </button>
                                                                    <button className="btn btn-sm btn-light text-danger" onClick={() => handleDeleteTemplate(t)}>
                                                                        <i className="bi bi-trash"></i>
                                                                    </button>
                                                                </td>
                                                            </tr>
                                                        ))
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                    {totalRecords > itemsPerPage && (
                                        <div className="mt-4">
                                            <Paginator
                                                page={currentPage}
                                                pageCount={pageCount}
                                                onPrevious={handlePrevious}
                                                onNext={handleNext}
                                                onPage={handlePageChange}
                                                maxPagesToShow={3}
                                            />
                                        </div>
                                    )}
                                </div>
                            );
                        })()}

                        {/* ── Channels Tab ───────────────────────── */}
                        {activeTab === 'channels' && (
                            <div className="row g-4">
                                <div className="col-12 col-lg-5">
                                    <div className="card border-0 shadow-sm h-100" style={{ borderRadius: theme.cardRadius }}>
                                        <div className="card-header bg-white border-bottom py-3 px-4 d-flex justify-content-between align-items-center">
                                            <h6 className="fw-bold mb-0">Configured Channels</h6>
                                            {availableTypes.length > 0 && (
                                                <button
                                                    className="btn btn-sm btn-primary"
                                                    onClick={() => { resetChannelForm(); setShowChannelForm(true); }}
                                                    style={{ background: theme.primary, border: 'none', borderRadius: theme.buttonRadius }}
                                                >
                                                    <i className="bi bi-plus-lg me-1"></i>Add
                                                </button>
                                            )}
                                        </div>
                                        <div className="card-body p-0">
                                            {channels.length === 0 ? (
                                                <div className="text-center text-muted py-5">
                                                    No channels configured.
                                                </div>
                                            ) : (
                                                <div className="list-group list-group-flush">
                                                    {channels.map(ch => (
                                                        <div key={ch.id} className={`list-group-item px-4 py-3 border-bottom ${editingChannel?.id === ch.id ? 'bg-light' : ''}`}>
                                                            <div className="d-flex justify-content-between align-items-center">
                                                                <div className="d-flex align-items-center gap-3">
                                                                    <i className={`bi ${CHANNEL_ICONS[ch.channel_type] || 'bi-bell'} fs-4`} style={{ color: ch.enabled ? theme.primary : '#94a3b8' }}></i>
                                                                    <div>
                                                                        <div className="fw-bold text-dark text-capitalize">
                                                                            {(ch.channel_type || '').replace(/_/g, ' ')}
                                                                            {ch.config?.provider && <span className="text-muted small ms-2 fw-normal">({ch.config.provider})</span>}
                                                                        </div>
                                                                        <div className="small text-muted">
                                                                            {ch.enabled ? <span className="text-success"><i className="bi bi-check-circle me-1"></i>Active</span> : <span><i className="bi bi-dash-circle me-1"></i>Disabled</span>}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                <div className="d-flex gap-1">
                                                                    <button className="btn btn-sm btn-outline-secondary border-0" onClick={() => handleToggleChannel(ch)} title={ch.enabled ? 'Disable' : 'Enable'}>
                                                                        <i className={`bi ${ch.enabled ? 'bi-toggle-on text-success' : 'bi-toggle-off text-muted'}`}></i>
                                                                    </button>
                                                                    <button className="btn btn-sm btn-outline-primary border-0" onClick={() => openEditChannel(ch)}>
                                                                        <i className="bi bi-pencil"></i>
                                                                    </button>
                                                                    <button className="btn btn-sm btn-outline-danger border-0" onClick={() => handleDeleteChannel(ch)}>
                                                                        <i className="bi bi-trash"></i>
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="col-12 col-lg-7">
                                    {showChannelForm ? (
                                        <div className="card border-0 shadow-sm" style={{ borderRadius: theme.cardRadius }}>
                                            <div className="card-header bg-white border-bottom py-3 px-4">
                                                <h6 className="fw-bold mb-0 text-primary">{editingChannel ? 'Edit Channel Details' : 'Configure New Channel'}</h6>
                                            </div>
                                            <div className="card-body p-4">
                                                <div className="mb-4">
                                                    <label className="fw-bold text-dark mb-2">Channel Type</label>
                                                    <select
                                                        className={`form-select ${channelErrors.channel_type ? 'is-invalid' : ''}`}
                                                        value={channelType}
                                                        onChange={e => {
                                                            setChannelType(e.target.value);
                                                            setChannelProvider('');
                                                            setChannelConfigValues({});
                                                            setChannelErrors({});
                                                        }}
                                                        disabled={!!editingChannel}
                                                        style={{ borderRadius: theme.inputRadius || '6px' }}
                                                    >
                                                        <option value="">Select type...</option>
                                                        {CHANNEL_TYPES.map(ct => {
                                                            const exists = configuredTypes.includes(ct.value) && (!editingChannel || editingChannel.channel_type !== ct.value);
                                                            const frozen = ct.frozen && !exists;
                                                            return (
                                                                <option key={ct.value} value={ct.value} disabled={exists || ct.frozen}>
                                                                    {ct.label}{exists ? ' — Already exists' : frozen ? ` — ${ct.frozenLabel}` : ''}
                                                                </option>
                                                            );
                                                        })}
                                                    </select>
                                                    <FieldError error={channelErrors.channel_type} />
                                                </div>

                                                {channelType && renderConfigFields()}

                                                {channelType && !CHANNEL_TYPES.find(c => c.value === channelType)?.hasConfig && (
                                                    <div className="alert alert-info py-3" style={{ borderRadius: '8px' }}>
                                                        <div className="d-flex align-items-center text-primary fw-bold mb-1">
                                                            <i className="bi bi-info-circle me-2"></i>No Configuration Required
                                                        </div>
                                                        <p className="mb-0 small text-dark">This channel operates using platform defaults. Simply save to activate it.</p>
                                                    </div>
                                                )}

                                                <div className="d-flex gap-2 mt-4 pt-3 border-top">
                                                    <button className="btn text-white px-4" onClick={handleSaveChannel} style={{ background: theme.primary, borderRadius: theme.buttonRadius }}>
                                                        {editingChannel ? 'Update Channel' : 'Save Channel'}
                                                    </button>
                                                    <button className="btn btn-light px-4" onClick={resetChannelForm} style={{ borderRadius: theme.buttonRadius }}>Cancel</button>
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="card border-0 shadow-sm h-100 d-flex align-items-center justify-content-center bg-light" style={{ borderRadius: theme.cardRadius, minHeight: 400 }}>
                                            <div className="text-center text-muted">
                                                <i className="bi bi-broadcast fs-1 mb-3 text-secondary opacity-50"></i>
                                                <h5>Select a channel</h5>
                                                <p className="small mb-0 mt-2">Choose an existing channel to edit or create a new one.</p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </>
                )
                }
            </div>

            {/* Template Modal Dialog */}
            {showTemplateModal && (
                <>
                    <div className="modal-backdrop fade show" style={{ zIndex: 1040 }}></div>
                    <div className="modal fade show d-block" tabIndex="-1" style={{ zIndex: 1050 }}>
                        <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
                            <div className="modal-content border-0 shadow" style={{ borderRadius: theme.cardRadius || '12px' }}>
                                <div className="modal-header border-bottom-0 bg-light py-3 px-4">
                                    <h5 className="modal-title fw-bold text-dark">
                                        <i className="bi bi-window-stack text-primary me-2"></i>
                                        {editingTemplate ? 'Edit Template' : 'Create Template'}
                                    </h5>
                                    <button type="button" className="btn-close" onClick={resetTemplateForm}></button>
                                </div>
                                <div className="modal-body px-4 py-4">
                                    <div className="row g-3 mb-4">
                                        <div className="col-12 col-md-6">
                                            <label className="fw-bold text-dark small mb-1">Template Name <span className="text-danger">*</span></label>
                                            <input className={`form-control ${templateErrors.name ? ' is-invalid' : ''}`}
                                                value={templateForm.name}
                                                onChange={e => setTemplateForm(f => ({ ...f, name: e.target.value }))}
                                                placeholder="e.g. Order Confirmation"
                                                style={{ borderRadius: theme.inputRadius || '6px' }} />
                                            <FieldError error={templateErrors.name} />
                                        </div>
                                        <div className="col-12 col-md-6">
                                            <label className="fw-bold text-dark small mb-1">Trigger Binding <span className="text-danger">*</span></label>
                                            <Select
                                                options={triggers.map(tr => ({ value: tr.id, label: `${tr.name}` }))}
                                                value={triggers.map(tr => ({ value: tr.id, label: `${tr.name}` })).find(o => o.value == templateForm.trigger) || null}
                                                onChange={opt => setTemplateForm(f => ({ ...f, trigger: opt ? opt.value : '' }))}
                                                isClearable
                                                placeholder="Search triggers..."
                                                classNamePrefix="react-select"
                                                menuPortalTarget={document.body}
                                                menuPosition="fixed"
                                                styles={{
                                                    control: (base, state) => ({
                                                        ...base,
                                                        borderRadius: theme.inputRadius || '6px',
                                                        borderColor: templateErrors.trigger ? '#dc3545' : base.borderColor,
                                                        boxShadow: state.isFocused ? (templateErrors.trigger ? '0 0 0 0.25rem rgba(220, 53, 69, 0.25)' : '0 0 0 0.25rem rgba(13, 110, 253, 0.25)') : 'none',
                                                    }),
                                                    menuPortal: base => ({ ...base, zIndex: 9999 }),
                                                }}
                                            />
                                            <FieldError error={templateErrors.trigger} />
                                        </div>
                                    </div>

                                    <div className="mb-4">
                                        <label className="fw-bold text-dark small mb-2 d-block">Delivery Channels <span className="text-danger">*</span></label>
                                        <div className="d-flex flex-wrap gap-2">
                                            {TEMPLATE_CHANNEL_TYPES.map(ct => (
                                                <label key={ct.value}
                                                    className={`d-flex align-items-center gap-2 px-3 py-2 rounded border small mb-0 ${ct.frozen ? 'bg-light text-muted border-light opacity-60' : templateForm.channels.includes(ct.value) ? 'border-primary bg-primary bg-opacity-10 text-primary fw-bold' : 'bg-white text-muted border-light'}`}
                                                    style={{ cursor: ct.frozen ? 'not-allowed' : 'pointer', transition: 'all 0.15s ease-in-out' }}
                                                    title={ct.frozen ? ct.frozenLabel : undefined}>
                                                    <input type="checkbox"
                                                        checked={templateForm.channels.includes(ct.value)}
                                                        onChange={() => !ct.frozen && handleTemplateChannelToggle(ct.value)}
                                                        disabled={ct.frozen}
                                                        className="form-check-input mt-0" />
                                                    <i className={`bi ${ct.icon}`}></i>
                                                    {ct.label}
                                                    {ct.frozen && <span className="ms-1 text-muted" style={{ fontSize: 10 }}>({ct.frozenLabel})</span>}
                                                </label>
                                            ))}
                                        </div>
                                        <FieldError error={templateErrors.channels} />
                                    </div>

                                    <div className="mb-3">
                                        <label className="fw-bold text-dark small mb-1">Subject / Header Template <span className="text-danger">*</span></label>
                                        <input className={`form-control ${templateErrors.subject_template ? ' is-invalid' : ''}`}
                                            value={templateForm.subject_template}
                                            onChange={e => setTemplateForm(f => ({ ...f, subject_template: e.target.value }))}
                                            placeholder="e.g. Order #{{record.id}} confirmed"
                                            style={{ borderRadius: theme.inputRadius || '6px' }} />
                                        <FieldError error={templateErrors.subject_template} />
                                    </div>
                                    <div className="mb-4">
                                        <label className="fw-bold text-dark small mb-1">Body Template <span className="text-danger">*</span></label>
                                        <textarea className={`form-control ${templateErrors.body_template ? ' is-invalid' : ''}`}
                                            rows={5}
                                            value={templateForm.body_template}
                                            onChange={e => setTemplateForm(f => ({ ...f, body_template: e.target.value }))}
                                            placeholder="e.g. Hi {{target_name}}, your order has been placed."
                                            style={{ borderRadius: theme.inputRadius || '6px' }} />
                                        <FieldError error={templateErrors.body_template} />
                                    </div>

                                    {/* Context Key Reference */}
                                    <div className="bg-light p-3 rounded border border-info border-opacity-25" style={{ fontSize: '13px' }}>
                                        <div className="fw-bold text-info mb-2 d-flex align-items-center">
                                            <i className="bi bi-code-slash me-2"></i>Data Binding Reference
                                        </div>
                                        <p className="text-muted mb-2">Use curly braces <code>{'{{key_name}}'}</code> in your templates to dynamically insert data when emails are sent.</p>

                                        {templateForm.trigger && triggers.find(t => t.id === templateForm.trigger) && contextKeys ? (
                                            <div className="row g-2 mt-2 pt-2 border-top">
                                                <div className="col-12 col-md-4">
                                                    <strong className="d-block mb-1 text-dark">System:</strong>
                                                    <ul className="list-unstyled ps-0 text-muted mb-0">
                                                        {contextKeys.application?.map(k => (
                                                            <li key={k.key} className="text-truncate" title={k.display_name}><code>{`{{${k.key}}}`}</code></li>
                                                        ))}
                                                    </ul>
                                                </div>
                                                <div className="col-12 col-md-4">
                                                    <strong className="d-block mb-1 text-dark">Users:</strong>
                                                    <ul className="list-unstyled ps-0 text-muted mb-0">
                                                        {contextKeys.user?.map(k => (
                                                            <li key={k.key} className="text-truncate" title={k.display_name}><code>{`{{${k.key}}}`}</code></li>
                                                        ))}
                                                    </ul>
                                                </div>
                                                <div className="col-12 col-md-4">
                                                    <strong className="d-block mb-1 text-dark">Record:</strong>
                                                    <ul className="list-unstyled ps-0 text-muted mb-0">
                                                        {(() => {
                                                            const trig = triggers.find(t => t.id === templateForm.trigger);
                                                            const evt = events.find(e => e.id === trig?.event);
                                                            if (!evt || !evt.event_type.startsWith('record.')) return <li className="text-light-50 fst-italic">N/A</li>;

                                                            const tblKeys = [];
                                                            if (evt.table_scope === 'all') {
                                                                Object.values(contextKeys.tables || {}).forEach(arr => tblKeys.push(...arr));
                                                            } else {
                                                                // If scoped, just flatmap the first matched logic.
                                                                // Note: For fully accurate dynamic fields, we would match evt.scoped_tables array 
                                                                // to contextKeys.tables.
                                                                Object.values(contextKeys.tables || {}).forEach(arr => tblKeys.push(...arr));
                                                            }
                                                            return tblKeys.slice(0, 4).map((k, i) => (
                                                                <li key={`tbl-${i}`} className="text-truncate" title={k.display_name}><code>{`{{${k.key}}}`}</code></li>
                                                            )).concat(tblKeys.length > 4 ? [<li key="more" className="fst-italic opacity-75">...and more</li>] : []);
                                                        })()}
                                                    </ul>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="text-muted mt-2 fst-italic">Select a trigger above to see which variables are guaranteed to be available.</div>
                                        )}
                                    </div>
                                </div>
                                <div className="modal-footer border-top-0 px-4 pb-4">
                                    <button className="btn btn-light border fw-bold px-4" onClick={resetTemplateForm} style={{ borderRadius: theme.buttonRadius }}>Cancel</button>
                                    <button className="btn text-white fw-bold px-4" onClick={handleSaveTemplate} style={{ background: theme.primary, borderRadius: theme.buttonRadius }}>
                                        {editingTemplate ? 'Update Changes' : 'Create Template'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}

